import { useEffect, useState, useRef } from 'react';
import { Coordinates, MapBounds } from '../types';
import { boundsOverlapRatio } from '../utils/boundsUtils';
import { detectViewportCountry } from '../utils/countryDetection';

export interface WildlifeMapObservation {
  id: string;
  species: string;
  category: 'Mammal' | 'Bird';
  location: Coordinates;
  observedAt: Date | null;
  observedCount?: number | null;
  behavior?: string | null;
  habitat?: string | null;
  precisionMeters?: number | null;
  locality?: string | null;
  municipality?: string | null;
  county?: string | null;
  datasetName?: string | null;
  institution?: string | null;
  detailUrl?: string | null;
  thumbnailUrl?: string | null;
}

const normalize = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const getSpecies = (obs: any): { species: string; category: 'Mammal' | 'Bird' } | null => {
  const names = [
    obs?.Name,
    obs?.vernacularName,
    obs?.vernacularNameNb,
    obs?.vernacularNameBokmal,
    obs?.taxonVernacularName,
    obs?.taxonName,
    obs?.speciesName,
    obs?.name,
    obs?.ScientificName,
    obs?.scientificName,
    obs?.taxon?.vernacularName,
    obs?.taxon?.vernacularNameNb,
    obs?.taxon?.vernacularNameBokmal,
    obs?.taxon?.scientificName,
    obs?.taxon?.name,
    obs?.species, // GBIF field
  ]
    .filter(Boolean)
    .map((v: any) => normalize(String(v)));

  const has = (s: string) => names.some(n => n.includes(s));

  if (has('elg') || has('alces alces')) return { species: 'Elg', category: 'Mammal' };
  if (has('hjort') || has('kronhjort') || has('cervus elaphus')) return { species: 'Hjort', category: 'Mammal' };
  if (has('radyr') || has('rådyr') || has('capreolus capreolus')) return { species: 'Radyr', category: 'Mammal' };
  if (has('rein') || has('ren') || has('rangifer tarandus')) return { species: 'Rein', category: 'Mammal' };
  if (has('rev') || has('rav') || has('vulpes vulpes')) return { species: 'Rev', category: 'Mammal' };
  if (has('grevling') || has('gravling') || has('meles meles')) return { species: 'Grevling', category: 'Mammal' };
  if (has('oter') || has('otter') || has('utter') || has('lutra lutra')) return { species: 'Oter', category: 'Mammal' };
  if (has('bever') || has('baver') || has('castor fiber')) return { species: 'Bever', category: 'Mammal' };

  if (has('havorn') || has('havørn') || has('havsorn') || has('haliaeetus albicilla')) return { species: 'Havorn', category: 'Bird' };
  if (has('kongeorn') || has('kongeørn') || has('kungsorn') || has('aquila chrysaetos')) return { species: 'Kongeorn', category: 'Bird' };
  if (has('ugle') || has('ugler') || has('uggla') || has('strigidae') || has('strigiformes')) return { species: 'Ugle', category: 'Bird' };
  if (has('tiur') || has('tjader') || has('tetrao urogallus')) return { species: 'Tiur', category: 'Bird' };
  if (has('orrfugl') || has('orre') || has('lyrurus tetrix')) return { species: 'Orrfugl', category: 'Bird' };
  if (has('trane') || has('traner') || has('trana') || has('grus grus')) return { species: 'Trane', category: 'Bird' };

  return null;
};

const parseDate = (obs: any): Date | null => {
  const raw =
    obs?.observedAt ||
    obs?.observationDate ||
    obs?.eventDate ||
    obs?.dateObserved ||
    obs?.createdAt ||
    obs?.date ||
    obs?.TrackDateTime;
  if (!raw) return null;
  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d;
};

const parseCount = (obs: any): number | null => {
  const raw = obs?.Count ?? obs?.count ?? obs?.individualCount;
  const n = toNumber(raw);
  return Number.isFinite(n) && n > 0 ? Math.round(n) : null;
};

const parsePrecision = (obs: any): number | null => {
  const n = toNumber(obs?.Precision ?? obs?.precision ?? obs?.locationPrecision ?? obs?.coordinateUncertaintyInMeters);
  return Number.isFinite(n) && n >= 0 ? Math.round(n) : null;
};

const parseThumbnail = (obs: any): string | null => {
  const list = obs?.ThumbImgUrls;
  if (Array.isArray(list) && list.length > 0) {
    const first = list.find((v) => typeof v === 'string' && v.trim().length > 0);
    return first || null;
  }
  if (typeof list === 'string' && list.trim().length > 0) return list;
  return null;
};

const toNumber = (value: unknown): number => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const n = Number(value.replace(',', '.').trim());
    return Number.isFinite(n) ? n : NaN;
  }
  return NaN;
};

const parseLocation = (obs: any): Coordinates | null => {
  const lat = toNumber(
    obs?.decimalLatitude ??
    obs?.lat ??
    obs?.latitude ??
    obs?.location?.lat ??
    obs?.location?.latitude ??
    obs?.Latitude ??
    obs?.coordinates?.lat ??
    obs?.coordinates?.latitude
  );
  const lon = toNumber(
    obs?.decimalLongitude ??
    obs?.lon ??
    obs?.long ??
    obs?.lng ??
    obs?.longitude ??
    obs?.location?.lon ??
    obs?.location?.lng ??
    obs?.location?.longitude ??
    obs?.Longitude ??
    obs?.coordinates?.lon ??
    obs?.coordinates?.lng ??
    obs?.coordinates?.longitude
  );

  if (!isFinite(lat) || !isFinite(lon)) return null;
  if (Math.abs(lat) > 90 || Math.abs(lon) > 180) return null;
  return { latitude: lat, longitude: lon };
};

const formatDateISO = (date: Date): string => date.toISOString().slice(0, 10);

const PROXY_URL = 'https://us-central1-fricamp-2810e.cloudfunctions.net/contentProxy';
const WILDLIFE_RADIUS_METERS = 2000; // Keep in sync with hiking badge radius.

const toWebMercator = (lat: number, lon: number): { x: number; y: number } => {
  const r = 6378137;
  return {
    x: (lon * Math.PI / 180) * r,
    y: Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI / 360))) * r
  };
};

const buildGmWktPolygon = (center: Coordinates, radiusMeters: number): string => {
  const m = toWebMercator(center.latitude, center.longitude);
  const x1 = m.x - radiusMeters;
  const x2 = m.x + radiusMeters;
  const y1 = m.y - radiusMeters;
  const y2 = m.y + radiusMeters;
  return `POLYGON((${x1} ${y1},${x2} ${y1},${x2} ${y2},${x1} ${y2},${x1} ${y1}))`;
};

const buildRoutePolygon = (route: Coordinates[], paddingMeters: number): string | null => {
  if (!route.length) return null;
  const projected = route
    .filter((p) => Number.isFinite(p.latitude) && Number.isFinite(p.longitude))
    .map((p) => toWebMercator(p.latitude, p.longitude));
  if (!projected.length) return null;

  let minX = projected[0].x;
  let maxX = projected[0].x;
  let minY = projected[0].y;
  let maxY = projected[0].y;

  projected.forEach((p) => {
    minX = Math.min(minX, p.x);
    maxX = Math.max(maxX, p.x);
    minY = Math.min(minY, p.y);
    maxY = Math.max(maxY, p.y);
  });

  minX -= paddingMeters;
  minY -= paddingMeters;
  maxX += paddingMeters;
  maxY += paddingMeters;
  return `POLYGON((${minX} ${minY},${maxX} ${minY},${maxX} ${maxY},${minX} ${maxY},${minX} ${minY}))`;
};

// --- Artsdatabanken URL (Norway) ---
const buildArtsdatabankenUrl = (center: Coordinates, routeGeometry?: Coordinates[] | null): string => {
  const to = new Date();
  const from = new Date();
  from.setMonth(from.getMonth() - 12);

  const params = new URLSearchParams();
  params.set('pageIndex', '0');
  params.set('pageSize', '600');
  params.set('fromDate', formatDateISO(from));
  params.set('toDate', formatDateISO(to));
  const routePolygon = routeGeometry && routeGeometry.length > 1
    ? buildRoutePolygon(routeGeometry, 400)
    : null;
  params.set('gmWktPolygon', routePolygon || buildGmWktPolygon(center, WILDLIFE_RADIUS_METERS));

  const sourceUrl = `https://artskart.artsdatabanken.no/publicapi/api/observations/list?${params.toString()}`;
  return `${PROXY_URL}?url=${encodeURIComponent(sourceUrl)}`;
};

// --- GBIF URL (Sweden / international) ---
// Scientific names for the species we track
const GBIF_SPECIES = [
  'Alces alces', 'Cervus elaphus', 'Capreolus capreolus', 'Rangifer tarandus',
  'Vulpes vulpes', 'Meles meles', 'Lutra lutra', 'Castor fiber',
  'Haliaeetus albicilla', 'Aquila chrysaetos', 'Tetrao urogallus',
  'Lyrurus tetrix', 'Grus grus',
];

const buildGbifUrl = (bounds: MapBounds): string => {
  const to = new Date();
  const from = new Date();
  from.setMonth(from.getMonth() - 12);

  // GBIF uses WGS84 WKT POLYGON for geometry filter
  const wkt = `POLYGON((${bounds.lonMin} ${bounds.latMin},${bounds.lonMax} ${bounds.latMin},${bounds.lonMax} ${bounds.latMax},${bounds.lonMin} ${bounds.latMax},${bounds.lonMin} ${bounds.latMin}))`;

  const params = new URLSearchParams();
  params.set('geometry', wkt);
  params.set('country', 'SE');
  params.set('eventDate', `${formatDateISO(from)},${formatDateISO(to)}`);
  params.set('limit', '300');
  params.set('hasCoordinate', 'true');
  params.set('occurrenceStatus', 'PRESENT');
  // Query each species separately would be too many requests — use taxon keys instead
  // GBIF doesn't support multiple scientificName in one query, so we use a broader approach:
  // Query by order/class and filter client-side (same as Artsdatabanken approach)
  // OR: make parallel requests per species group
  // Most efficient: query mammals + birds separately
  return `https://api.gbif.org/v1/occurrence/search?${params.toString()}`;
};

// GBIF taxon keys for efficient querying (2 requests instead of 13)
// Mammalia class key: 359 — filters client-side to our species
// Aves class key: 212 — filters client-side to our species
const GBIF_TAXON_GROUPS = [
  { taxonKey: '359', label: 'Mammalia' },  // All mammals
  { taxonKey: '212', label: 'Aves' },      // All birds
];

const fetchGbifWildlife = async (
  bounds: MapBounds,
  signal: AbortSignal,
  countryCode: string = 'SE',
): Promise<any[]> => {
  const to = new Date();
  const from = new Date();
  from.setMonth(from.getMonth() - 12);
  const dateRange = `${formatDateISO(from)},${formatDateISO(to)}`;
  const wkt = `POLYGON((${bounds.lonMin} ${bounds.latMin},${bounds.lonMax} ${bounds.latMin},${bounds.lonMax} ${bounds.latMax},${bounds.lonMin} ${bounds.latMax},${bounds.lonMin} ${bounds.latMin}))`;

  // 2 requests instead of 13: mammals + birds, filtered client-side by getSpecies()
  const promises = GBIF_TAXON_GROUPS.map(async ({ taxonKey }) => {
    try {
      const params = new URLSearchParams({
        geometry: wkt,
        country: countryCode,
        eventDate: dateRange,
        taxonKey,
        limit: '300',
        hasCoordinate: 'true',
        occurrenceStatus: 'PRESENT',
      });
      const res = await fetch(`https://api.gbif.org/v1/occurrence/search?${params}`, {
        signal,
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return [];
      const json = await res.json();
      return json.results || [];
    } catch {
      return [];
    }
  });

  const results = await Promise.all(promises);
  return results.flat();
};

const mapObservation = (obs: any, idx: number): WildlifeMapObservation | null => {
  const sp = getSpecies(obs);
  const loc = parseLocation(obs);
  if (!sp || !loc) return null;
  return {
    id: String(obs?.Id || obs?.id || obs?.key || obs?.gbifID || obs?.observationId || `wildlife_${idx}_${sp.species}`),
    species: sp.species,
    category: sp.category,
    location: loc,
    observedAt: parseDate(obs),
    observedCount: parseCount(obs),
    behavior: typeof obs?.Behavior === 'string' ? obs.Behavior : null,
    habitat: typeof obs?.Habitat === 'string' ? obs.Habitat : null,
    precisionMeters: parsePrecision(obs),
    locality: typeof (obs?.Locality ?? obs?.locality) === 'string' ? (obs?.Locality ?? obs?.locality) : null,
    municipality: typeof (obs?.Municipality ?? obs?.municipality) === 'string' ? (obs?.Municipality ?? obs?.municipality) : null,
    county: typeof (obs?.County ?? obs?.county ?? obs?.stateProvince) === 'string' ? (obs?.County ?? obs?.county ?? obs?.stateProvince) : null,
    datasetName: typeof (obs?.DatasetName ?? obs?.datasetName) === 'string' ? (obs?.DatasetName ?? obs?.datasetName) : null,
    institution: typeof (obs?.Institution ?? obs?.publishingOrgKey) === 'string' ? (obs?.Institution ?? obs?.publishingOrgKey) : null,
    detailUrl: typeof obs?.DetailUrl === 'string' ? obs.DetailUrl
      : (typeof obs?.ObsUrl === 'string' ? obs.ObsUrl
      : (obs?.key ? `https://www.gbif.org/occurrence/${obs.key}` : null)),
    thumbnailUrl: parseThumbnail(obs)
  };
};

export const useWildlifeLayer = (
  active: boolean,
  zoom: number,
  center: Coordinates,
  currentViewBounds: MapBounds | null,
  routeGeometry?: Coordinates[] | null
) => {
  const [wildlife, setWildlife] = useState<WildlifeMapObservation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const centerRef = useRef(center);
  centerRef.current = center;
  const lastFetchedBoundsRef = useRef<MapBounds | null>(null);

  useEffect(() => {
    if (!active || zoom < 10 || !currentViewBounds) {
      setIsLoading(false);
      if (!active) { setWildlife([]); setError(false); lastFetchedBoundsRef.current = null; }
      return;
    }

    if (lastFetchedBoundsRef.current && boundsOverlapRatio(lastFetchedBoundsRef.current, currentViewBounds) > 0.7) {
      return;
    }

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setIsLoading(true);
      try {
        // Country-aware: Artsdatabanken for Norway, GBIF for Sweden
        const country = detectViewportCountry(currentViewBounds);
        let rawList: any[] = [];

        if (country === 'SE' || country === 'DK') {
          // GBIF for Sweden/Denmark — no proxy needed, CORS-friendly
          rawList = await fetchGbifWildlife(currentViewBounds, controller.signal, country);
        } else {
          // Artsdatabanken for Norway (and border/unknown areas)
          const response = await fetch(buildArtsdatabankenUrl(centerRef.current, routeGeometry), {
            signal: controller.signal,
            headers: { Accept: 'application/json' }
          });
          if (!response.ok) throw new Error(`wildlife_layer_${response.status}`);
          const json = await response.json();

          rawList = Array.isArray(json)
            ? json
            : Array.isArray(json?.data)
              ? json.data
              : Array.isArray(json?.Observations)
                ? json.Observations
                : Array.isArray(json?.results)
                  ? json.results
                  : Array.isArray(json?.observations)
                    ? json.observations
                    : [];
        }

        const mapped = (rawList
          .map((obs, idx) => mapObservation(obs, idx))
          .filter(Boolean) as WildlifeMapObservation[])
          // Belt-and-suspenders: parseLocation already rejects bad coordinates,
          // but a malformed payload (e.g. 429-truncated, proxy hiccup) could in
          // theory slip an entry through. Filtering here keeps the public
          // contract — every WildlifeMapObservation has a finite location —
          // so consumers can safely read w.location.latitude without guards.
          .filter((w) =>
            !!w.location &&
            Number.isFinite(w.location.latitude) &&
            Number.isFinite(w.location.longitude)
          );

        setWildlife(mapped);
        setError(false);
        lastFetchedBoundsRef.current = currentViewBounds;
      } catch (err: any) {
        if (err?.name !== 'AbortError') {
          console.warn('Wildlife layer fetch failed', err);
          setWildlife([]);
          setError(true);
        }
      } finally {
        setIsLoading(false);
      }
    }, 1000);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [active, zoom, currentViewBounds, routeGeometry]);

  return { wildlife, isLoading, error };
};
